import { useEffect, useMemo } from 'react';

import type { UseQueryResult } from '@tanstack/react-query';

import type { GetEmailByMessageIdResponse } from 'wg-api-sdk';

import type { EmailMessage } from '@/features/email/types';
import useParties from '@/features/party/queries/hooks/useParties';
import useParty from '@/features/party/queries/hooks/useParty';
import type { PartyStatus } from '@/features/party/types';
import useAuth from '@/hooks/useAuth';
import hasItems from '@/lib/utils/hasItems';
import useLinkedPartyStore from '@/stores/useLinkedPartyStore';

import usePartyExtraction from './usePartyExtraction';

export default function usePartyData(
  emailData: Maybe<EmailMessage>,
  analyzedEmail: UseQueryResult<GetEmailByMessageIdResponse>['data'],
) {
  const { distribution } = useAuth();

  const partyIdByMessageId = useLinkedPartyStore(
    (state) => state.partyIdByMessageId,
  );

  const distributionId = distribution?.id;
  const fromAddresses = emailData?.fromCandidates;

  const partySearchEnabled = Boolean(distributionId) && hasItems(fromAddresses);
  const { data: parties, isLoading: isLoadingParties } = useParties(
    distributionId ?? '',
    { emails: fromAddresses ?? [] },
    {
      enabled: partySearchEnabled,
    },
  );

  const { extractedParty, isExtractingParty, startPartyExtractionFlow } =
    usePartyExtraction(emailData);

  const userLinkedPartyId = emailData?.messageId
    ? partyIdByMessageId[emailData.messageId]
    : undefined;

  const analysisLinkedPartyId = analyzedEmail?.analysis?.party_id;

  const { data: linkedParty, isLoading: isLinkedPartyLoading } = useParty(
    userLinkedPartyId ?? analysisLinkedPartyId ?? '',
    {
      enabled: Boolean(userLinkedPartyId) || Boolean(analysisLinkedPartyId),
    },
  );

  const status: PartyStatus = useMemo(() => {
    if (userLinkedPartyId || analysisLinkedPartyId) {
      if (isLinkedPartyLoading) return 'Loading';

      return 'Selected';
    }

    if (isLoadingParties) return 'Loading';

    if (hasItems(parties)) {
      if (parties.length === 1) return 'Selected';

      return 'MultipleFound';
    }

    return 'NoResults';
  }, [
    userLinkedPartyId,
    analysisLinkedPartyId,
    isLoadingParties,
    parties,
    isLinkedPartyLoading,
  ]);

  const party = useMemo(() => {
    if (userLinkedPartyId || analysisLinkedPartyId) return linkedParty;

    if (hasItems(parties) && parties.length === 1) return parties[0];

    return undefined;
  }, [userLinkedPartyId, analysisLinkedPartyId, linkedParty, parties]);

  useEffect(() => {
    if (!distribution?.id) return;

    // Do party extraction flow when no results
    if (status === 'NoResults') startPartyExtractionFlow();
  }, [distribution?.id, startPartyExtractionFlow, status]);

  return {
    parties,
    isLoadingParties,
    status,
    party,
    extractedParty,
    isExtractingParty,
    startPartyExtractionFlow,
  };
}
